import { BentoCard, BentoCardContent } from '@/app/_components/core/bento-card';
import { RespImage } from '@/app/_components/core/resp-image';
import { RichText, type RichTextClassConfig } from '@/app/_components/core/rich-text';
import { SmartLink } from '@/app/_components/core/smart-link';
import { CardCarouselElement } from '@/app/_components/shared/card-carousel/card-carousel.types';
import { richTextHasInlineLinks } from '@/app/_components/shared/utils/component-helpers';
import { cn, emptyToUndefined } from '@/app/_lib/utils';

const defaultCardRichText: RichTextClassConfig = {
  copytext: 'text-sm lg:text-sm font-normal text-muted-foreground',
  list: 'text-sm lg:text-sm font-normal text-muted-foreground',
  link: 'text-muted-foreground',
};

interface DefaultCardProps {
  card: CardCarouselElement;
  className?: string;
  priority?: boolean;
  trackId?: string;
}

export function DefaultCard({ card, className, priority, trackId }: DefaultCardProps) {
  // Don't wrap card in link if text contains inline links (nested links = invalid HTML)
  const hasInlineLinks = richTextHasInlineLinks(card.text);
  const isLinked = card.cta?.linkUrl && !hasInlineLinks;

  const CardWrapper = (
    <BentoCard as="article" className={cn('group h-full', className)}>
      {card.image?.srcsetCandidates &&
      Array.isArray(card.image.srcsetCandidates) ? (
        <div className="relative aspect-video w-full overflow-hidden bg-muted">
          <RespImage
            srcsetCandidates={card.image.srcsetCandidates}
            alt={emptyToUndefined(card.image.altText) ?? emptyToUndefined(card.title) ?? ''}
            title={card.image.title ?? undefined}
            sizes="(min-width: 1920px) 391px, (min-width: 1280px) 319px, (min-width: 1024px) 262px, (min-width: 768px) 318px, (min-width: 640px) 482px, calc((100vw - 16px) * 0.8 - 24px * 0.2)"
            priority={priority}
            className={cn(
              'size-full  object-cover transition-transform duration-300',
              isLinked && '[@media(hover:hover)]:group-hover:scale-105'
            )}
          />
        </div>
      ) : card.icon?.url ? (
        <div className="relative flex aspect-4/3 w-full items-center justify-center overflow-hidden bg-muted">
          {/* eslint-disable-next-line @next/next/no-img-element -- fallback for icon-URL cards without srcset; the RespImage path above handles optimized images */}
          <img
            src={card.icon.url}
            alt={emptyToUndefined(card.icon.altText) ?? emptyToUndefined(card.title) ?? ''}
            width={400}
            height={300}
            className={cn(
              'size-full  object-cover transition-transform duration-300',
              isLinked && '[@media(hover:hover)]:group-hover:scale-105'
            )}
          />
        </div>
      ) : null}
      <BentoCardContent className="flex grow flex-col gap-2 py-6">
        {card.title && (
          <h3 className="text-lg/normal  font-semibold text-card-foreground">
            {card.title}
          </h3>
        )}
        {card.text && (
          <RichText classConfig={defaultCardRichText}>
            {card.text}
          </RichText>
        )}
        {card.detailDescription && (
          <RichText className="mt-auto" classConfig={defaultCardRichText}>
            {card.detailDescription}
          </RichText>
        )}
      </BentoCardContent>
    </BentoCard>
  );

  if (card.cta?.linkUrl && !hasInlineLinks) {
    return (
      <SmartLink
        href={card.cta.linkUrl}
        target={card.cta.linkTarget}
        download={card.cta.download}
        className="block h-full p-0.5"
        data-track-id={trackId}
      >
        {CardWrapper}
      </SmartLink>
    );
  }

  return CardWrapper;
}
